import { computed, nextTick, onMounted, onUnmounted, ref, type Ref } from 'vue'

const MAX_FOLDER_DEPTH = 4

export function useBreadcrumb({
  path,
  componentRef,
  containerRef,
}: {
  path: string[]
  componentRef: Ref<HTMLDivElement | null>
  containerRef: Ref<HTMLDivElement | null>
}) {
  const containerWidth = ref(0)
  const oldContainerWidth = ref(0)
  const componentWidth = ref(0)
  const clientWidth = ref(0)

  // Original number of locations we should hide (at best, we should only be showing 4 locations in the breadcrumb)
  const overflowingPathCount =
    path.length > MAX_FOLDER_DEPTH ? path.length - MAX_FOLDER_DEPTH : 0

  // Same as above, except this is dynamic and can change.
  const numberOfHiddenFolders = ref(overflowingPathCount)

  const updateWidth = () => {
    oldContainerWidth.value = containerWidth.value || 0
    containerWidth.value = containerRef.value?.clientWidth || 0
    componentWidth.value = componentRef.value?.clientWidth || 0

    adjustBreadcrumbPath()
  }

  const adjustBreadcrumbPath = async () => {
    // Find out whether the client is growing or shrinking
    const oldClientWidth = clientWidth.value

    clientWidth.value = document.body.clientWidth

    const isClientGrowing =
      oldClientWidth !== 0 && oldClientWidth < clientWidth.value
    const isClientShrinking =
      oldClientWidth === 0 || oldClientWidth >= clientWidth.value

    // If the client is growing, figure out if there is enough space to show additional locations in the breadcrumb
    if (isClientGrowing) {
      if (oldContainerWidth.value > containerWidth.value) {
        return await reset()
      }

      // Create a div node with the next location to show
      const div = document.createElement('div')
      div.textContent = path[numberOfHiddenFolders.value - 1]

      // Create a duplicate component node that will include an additional location
      const clonedComponentNode = componentRef.value?.cloneNode(
        true
      ) as HTMLDivElement
      clonedComponentNode.classList.add('invisible', 'absolute', 'top-[-100%]')
      clonedComponentNode.appendChild(div)
      containerRef.value?.appendChild(clonedComponentNode)

      // Compare new cloned component node's width with container, if it's still smaller, show the new location
      if (clonedComponentNode.clientWidth < containerWidth.value) {
        numberOfHiddenFolders.value = Math.max(
          --numberOfHiddenFolders.value,
          overflowingPathCount
        )
      }

      clonedComponentNode.remove()
    }

    // if the client is shrinking and the component's width is greater than the container's width, remove visible locations in breadcrumb path
    if (isClientShrinking) {
      if (oldContainerWidth.value < containerWidth.value) {
        return await reset()
      }

      if (componentWidth.value >= containerWidth.value) {
        const nextNumberOfHiddenFolders = ++numberOfHiddenFolders.value
        numberOfHiddenFolders.value = Math.min(
          nextNumberOfHiddenFolders,
          path.length - 1
        )
      }
    }
  }

  onMounted(async () => {
    // Initialize Breadcrumb with proper cut-off
    await reset()

    window.addEventListener('resize', updateWidth)
  })

  onUnmounted(() => {
    window.removeEventListener('resize', updateWidth)
  })

  const reset = async () => {
    numberOfHiddenFolders.value = overflowingPathCount

    for (const _ of path) {
      updateWidth()
      await nextTick()
    }
  }

  const visibleLocations = computed(() =>
    path.slice(numberOfHiddenFolders.value)
  )

  const hiddenLocations = computed(() =>
    path.slice(0, numberOfHiddenFolders.value)
  )

  return {
    visibleLocations,
    hiddenLocations,
  }
}
